import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { computeTotals, buildItems, fmtEur, salaExclusiveItems, legacySalaToNew, foodItems, legacyFoodToNew } from "@/lib/quote";
import { InfoSteps } from "@/components/wizard/InfoSteps";
import { ProductStep } from "@/components/wizard/ProductStep";
import { FoodStep } from "@/components/wizard/FoodStep";
import { SummaryStep } from "@/components/wizard/SummaryStep";

const STEPS = ["Contatti", "Evento", "Ospiti", "Bar", "Food", "Extra", "Riepilogo"];

const EMPTY = {
  contact: { name: "", phone: "", email: "", notes: "" },
  event: { type: "", type_custom: "", extra_hours: 0, date: "", time_start: "18:00", time_end: "21:00", location: "Bliss Café" },
  guests: { adults: 20, teens: 0, children: 0, dietary: "" },
  sala: { rooms: "interna", excl_interna: false, excl_deorsi: false, assignment: { adulti: "interna", ragazzi: "interna", bambini: "interna" } },
  food: { groups: { adulti: { service: "buffet", budget_pp: 0 }, ragazzi: { service: "buffet", budget_pp: 0 }, bambini: { service: "buffet", budget_pp: 0 } }, torta: "nessuna" },
  bar: { escludi_ragazzi: false, escludi_bambini: false },
};

export default function Wizard() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState(EMPTY);
  const [selections, setSelections] = useState({});
  const [catalog, setCatalog] = useState([]);
  const [config, setConfig] = useState({ durata_standard_ore: 3, tariffa_extra_ora: 0 });
  const [resumeToken, setResumeToken] = useState(token || null);
  const [submitted, setSubmitted] = useState(false);
  const [welcome, setWelcome] = useState(!token);
  const tapRef = useRef({ count: 0, last: 0 });

  useEffect(() => {
    api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => toast.error("Errore caricamento listino"));
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!token) return;
    api.get(`/quotes/resume/${token}`).then((r) => {
      const q = r.data;
      setData({ contact: q.contact, event: { ...EMPTY.event, ...q.event }, guests: { ...EMPTY.guests, ...q.guests }, sala: legacySalaToNew(q.sala, EMPTY.sala), food: legacyFoodToNew(q.food, EMPTY.food), bar: q.bar?.escludi_ragazzi !== undefined ? q.bar : EMPTY.bar });
      const sel = {};
      q.items.forEach((it) => { sel[it.product_id] = it.unit === "persona" ? 1 : it.qty; });
      setSelections(sel);
      setResumeToken(token);
      toast.success("Preventivo recuperato, puoi continuare da dove eri rimasto");
    }).catch((e) => toast.error(apiError(e)));
  }, [token]);

  const guestsCount = (Number(data.guests.adults) || 0) + (Number(data.guests.teens) || 0) + (Number(data.guests.children) || 0);
  const teensN = Number(data.guests.teens) || 0;
  const childrenN = Number(data.guests.children) || 0;
  const barGuests = (Number(data.guests.adults) || 0) + (data.bar.escludi_ragazzi ? 0 : teensN) + (data.bar.escludi_bambini ? 0 : childrenN);
  const barExcluded = (data.bar.escludi_ragazzi ? teensN : 0) + (data.bar.escludi_bambini ? childrenN : 0);
  const extraHours = Number(data.event.extra_hours) || 0;
  const softDrinkPrice = catalog.find((p) => p.name === "Soft Drink")?.price || 0;
  const items = buildItems(catalog, selections, guestsCount, barGuests);
  if (barExcluded > 0) {
    const softDrink = catalog.find((p) => p.name === "Soft Drink");
    if (softDrink) items.push({ product_id: "softdrink-esclusi", name: "Soft Drink (ragazzi/bambini esclusi dal bar)", category: "Bar", price: softDrink.price, vat: softDrink.vat, unit: "persona", qty: barExcluded, discount: 0 });
  }
  if (extraHours > 0 && config.tariffa_extra_ora > 0) {
    items.push({
      product_id: "supplemento-orario",
      name: `Ora aggiuntiva (oltre le ${config.durata_standard_ore} ore incluse)`,
      category: "Extra",
      price: config.tariffa_extra_ora,
      vat: 22,
      unit: "fisso",
      qty: extraHours,
      discount: 0,
    });
  }
  items.push(...salaExclusiveItems(data.sala, config));
  const foodIt = foodItems(data.food, data.guests);
  items.push(...foodIt);
  const animProduct = catalog.find((p) => p.name === "Animazione Bambini");
  const animMandatory = (Number(data.guests.children) || 0) > 6;
  if (animMandatory && animProduct && !items.some((i) => i.product_id === animProduct.id)) {
    items.push({ product_id: animProduct.id, name: `${animProduct.name} (obbligatoria oltre 6 bambini)`, category: animProduct.category, price: animProduct.price, vat: animProduct.vat, unit: "fisso", qty: 1, discount: 0 });
  }
  const animPriceVal = animProduct?.price || 70;
  const totals = computeTotals(items);

  useEffect(() => {
    const [h, m] = (data.event.time_start || "18:00").split(":").map(Number);
    const tot = (h * 60 + m + ((Number(config.durata_standard_ore) || 3) + extraHours) * 60) % 1440;
    const end = `${String(Math.floor(tot / 60)).padStart(2, "0")}:${String(tot % 60).padStart(2, "0")}`;
    if (end !== data.event.time_end) setData((d) => ({ ...d, event: { ...d.event, time_end: end } }));
  }, [data.event.time_start, extraHours, config.durata_standard_ore]); // eslint-disable-line react-hooks/exhaustive-deps

  const logoTap = () => {
    const now = Date.now();
    if (now - tapRef.current.last > 1500) tapRef.current.count = 0;
    tapRef.current.count += 1;
    tapRef.current.last = now;
    if (tapRef.current.count >= 3) navigate("/gestione");
  };

  const saveDraft = useCallback(async (status = "bozza") => {
    const payload = { ...data, items, status };
    if (resumeToken) {
      const r = await api.put(`/quotes/resume/${resumeToken}`, payload);
      return r.data;
    }
    const r = await api.post("/quotes", payload);
    setResumeToken(r.data.resume_token);
    return r.data;
  }, [data, items, resumeToken]);

  const validateStep = () => {
    if (step === 0 && (!data.contact.name || !data.contact.phone)) return "Inserisci nome e telefono";
    if (step === 1 && (!data.event.type || !data.event.date)) return "Seleziona tipo evento e data";
    if (step === 1 && data.event.type === "Altro" && !data.event.type_custom) return "Descrivi il tuo evento nella casella";
    if (step === 2 && guestsCount < 1) return "Indica almeno un ospite";
    return null;
  };

  const next = async () => {
    const err = validateStep();
    if (err) return toast.error(err);
    if (step === 1) {
      try {
        const r = await api.get(`/availability/${data.event.date}`);
        if (!r.data.available) return toast.error("Data non disponibile: è già occupata da un altro evento. Scegli un'altra data.");
      } catch {
        // disponibilità non verificabile: si procede, il backend rivalida all'invio
      }
    }
    if (step >= 1) saveDraft("bozza").catch(() => {});
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo(0, 0);
  };

  const update = (section, field, value) =>
    setData((d) => ({
      ...d,
      [section]: field === null ? { ...d[section], ...value } : { ...d[section], [field]: value },
    }));

  if (welcome)
    return (
      <div className="min-h-screen bg-[#2C1A11] flex flex-col items-center justify-center px-6 text-center">
        <button data-testid="logo-3tap" onClick={logoTap} className="select-none">
          <img src="/logo.png" alt="Bliss Café" className="h-28 w-auto mx-auto mb-8" draggable="false" />
        </button>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#FAF7F2] max-w-xl leading-tight">Il tuo evento al Bliss Café</h1>
        <p className="text-base text-[#FAF7F2]/60 mt-4 max-w-md">
          Compleanni, lauree, aperitivi aziendali e feste private. Componi il tuo preventivo in 2 minuti: ti rispondiamo su WhatsApp.
        </p>
        <button data-testid="start-wizard-button" onClick={() => setWelcome(false)}
          className="mt-10 bg-[#C85A32] hover:bg-[#a94a28] text-white text-lg font-bold rounded-full px-10 py-4 transition-colors">
          Crea il tuo preventivo →
        </button>
        <p className="text-xs text-[#FAF7F2]/40 mt-6">Gratis e senza impegno · totale indicativo in tempo reale</p>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#FAF7F2] pb-32">
      <header className="bg-[#2C1A11] text-[#FAF7F2] py-5 px-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <button data-testid="logo-3tap" onClick={logoTap} className="flex items-center gap-3 select-none">
            <img src="/logo.png" alt="Bliss Café" className="h-14 w-auto" draggable="false" />
            <span className="text-xs text-[#FAF7F2]/60 self-end pb-1">Configuratore preventivi eventi</span>
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 mt-6">
        <div data-testid="wizard-step-indicator" className="flex items-center gap-1 mb-8 overflow-x-auto pb-1">
          {STEPS.map((s, i) => (
            <button key={s} onClick={() => i < step && setStep(i)}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full whitespace-nowrap transition-colors ${
                i === step ? "bg-[#C85A32] text-white" : i < step ? "bg-[#2C1A11] text-white cursor-pointer" : "bg-white text-[#8a7a6d] border"}`}>
              <span>{i + 1}</span><span className="hidden sm:inline">{s}</span>
            </button>
          ))}
        </div>

        <div className="fade-in" key={step}>
          {step <= 2 && <InfoSteps step={step} data={data} update={update} config={config} extraHours={extraHours} guestsCount={guestsCount} animMandatory={animMandatory} animPrice={animPriceVal} />}
          {step === 3 && (
            <div className="space-y-4">
              {(teensN > 0 || childrenN > 0) && (
                <div data-testid="bar-exclusions-box" className="bg-white rounded-2xl border p-5 space-y-3">
                  <div className="text-sm font-semibold text-[#2C1A11]">Chi includiamo nei servizi bar?</div>
                  {teensN > 0 && (
                    <label className="flex items-center gap-2.5 text-sm cursor-pointer">
                      <input data-testid="bar-escludi-ragazzi" type="checkbox" className="w-5 h-5 accent-[#C85A32]" checked={data.bar.escludi_ragazzi} onChange={(e) => update("bar", "escludi_ragazzi", e.target.checked)} />
                      Escludi i <b>ragazzi</b> ({teensN}) dai servizi bar — avranno automaticamente Soft Drink
                    </label>
                  )}
                  {childrenN > 0 && (
                    <label className="flex items-center gap-2.5 text-sm cursor-pointer">
                      <input data-testid="bar-escludi-bambini" type="checkbox" className="w-5 h-5 accent-[#C85A32]" checked={data.bar.escludi_bambini} onChange={(e) => update("bar", "escludi_bambini", e.target.checked)} />
                      Escludi i <b>bambini</b> ({childrenN}) dai servizi bar — avranno automaticamente Soft Drink
                    </label>
                  )}
                  {barExcluded > 0 && (
                    <div data-testid="bar-exclusion-recap" className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
                      I servizi bar "a persona" verranno calcolati per <b>{barGuests} ospiti</b>; per i {barExcluded} esclusi è stato aggiunto il <b>Soft Drink</b> ({fmtEur(softDrinkPrice * barExcluded)}).
                    </div>
                  )}
                </div>
              )}
              <ProductStep title="Bar & Beverage" catalog={catalog} categories={["Bar"]} selections={selections} setSelections={setSelections} guestsCount={barGuests} />
            </div>
          )}
          {step === 4 && <FoodStep data={data} update={update} />}
          {step === 5 && <ProductStep title="Sala, Intrattenimento & Extra" catalog={catalog} categories={["Sala", "Intrattenimento", "Extra"]} selections={selections} setSelections={setSelections} guestsCount={guestsCount} showQty={(p) => p.name === "Animazione Bambini"} />}
          {step === 6 && <SummaryStep data={data} items={items} totals={totals} config={config} saveDraft={saveDraft} resumeToken={resumeToken} submitted={submitted} setSubmitted={setSubmitted} />}
        </div>
      </div>

      {!submitted && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#2C1A11] text-white px-4 py-3 shadow-2xl z-40">
          <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/50">Totale indicativo (IVA incl.)</div>
              <div data-testid="wizard-live-total-price" className="font-mono-num text-xl font-bold text-[#F0B429]">{fmtEur(totals.total)}</div>
            </div>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <Button data-testid="wizard-prev-button" variant="ghost" className="text-white hover:bg-white/10" onClick={() => setStep((s) => s - 1)}>
                  <ChevronLeft className="w-4 h-4 mr-1" /> Indietro
                </Button>
              )}
              {step < STEPS.length - 1 && (
                <Button data-testid="wizard-next-button" className="bg-[#C85A32] hover:bg-[#a94a28] rounded-full px-6" onClick={next}>
                  Avanti <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
